import * as React from 'react';
import { every, isNil, map } from 'es-toolkit/compat';

import { RenderRange, getUnitLabel, isPriceRange, isZero } from 'utils/priceUtils';
import { assertNever } from '@tock/shared/utils/typeUtils';
import { consumer, core } from '@tock/coretypes';
import { isEventType } from 'utils/offerings';
import { useExperimentVariant } from 'hooks/useExperiment';
import { useFeatureFlag } from '@tock/shared/utils/featureFlagContext';

interface Props {
  type?: consumer.Offering.Type;
  price?: consumer.IOfferedPriceSummary;
  descriptiveVariety?: core.DescriptiveVariety;
  priceDistribution?: core.TicketPriceInformation.PriceDistribution | null;
}

export const getIsRequiredSupplementZero = (price?: consumer.IOfferedPriceSummary) => {
  if (isNil(price)) {
    return false;
  }
  return (
    price?.supplements?.length === 0 ||
    every(price.supplements, (supplementPrice) => isZero(supplementPrice.price))
  );
};

export const hasPriceRanges = (price?: consumer.OfferedPriceSummary) => {
  if (isNil(price)) {
    return false;
  }
  const isTicketPriceZero = isZero(price.pricePerPerson);
  const isRequiredSupplementZero = getIsRequiredSupplementZero(price);

  return !isTicketPriceZero || !isRequiredSupplementZero;
};

/**
 * Lookup price distribution for a particular offering, defaulting to PER_TICKET
 * This logic was borrowed from OfferingDetailsPrice and copied here so we could
 * use it on the offering detail page too.
 */
export const getPriceDistribution = (
  type: consumer.Offering.Type,
  price: consumer.IOfferedPriceSummary
) =>
  type === 'DEPOSIT'
    ? isPriceRange(price.pricePerPerson)
      ? price.partyRangeConfigs?.[0].ticketPriceInformation?.priceDistribution
      : (price.partyRangeConfigs?.find(
          (config) => price.pricePerPerson?.minCents === config.ticketPriceInformation?.amountCents
        )?.ticketPriceInformation?.priceDistribution ?? 'PER_TICKET')
    : 'PER_TICKET';

export const PriceRange: React.FunctionComponent<Props> = ({
  descriptiveVariety,
  price,
  type,
  priceDistribution,
}) => {
  const disableDepositExperiment = useFeatureFlag('CONSUMER_DISABLE_DEPOSIT_EXPERIMENTS');
  const minimumSpendExperiment =
    useExperimentVariant('DepositDemandExperiment') === 2 && !disableDepositExperiment;

  if (isNil(price) || !price.pricePerPerson) {
    return null;
  }

  const unitLabel = getUnitLabel(priceDistribution ?? 'PER_TICKET');

  const getName = () => {
    if (!type || type === 'PRIX_FIXE') {
      return `per ${unitLabel}`;
    } else if (type === 'DEPOSIT') {
      return minimumSpendExperiment
        ? `minimum spend per ${unitLabel}`
        : `deposit ${
            (price?.partyRangeConfigs?.length ?? 0) > 1 && isPriceRange(price.pricePerPerson)
              ? 'based on party size'
              : priceDistribution === 'PER_PARTY'
                ? `per party`
                : `per ${unitLabel}`
          }`;
    } else if (isEventType(type)) {
      return 'per ticket';
    } else if (type === 'ORDER_DELIVERY' || type === 'ORDER_PICKUP') {
      if (descriptiveVariety === 'TOGO_PACKAGE') {
        return 'per package';
      } else if (descriptiveVariety === 'TOGO_A_LA_CARTE') {
        return '';
      }
      return `per ${unitLabel}`; // TODO(jacob) revisit
    }
    return '';
  };

  const getPriceRangeForSupplementPrice = (supplementPrice: consumer.OfferedSupplementPrice) => {
    switch (supplementPrice.type) {
      case 'PER_PARTY': {
        return <RenderRange text={'per party'} price={supplementPrice.price} />;
      }
      case 'UNLIMITED': {
        return <RenderRange text={'required add-ons'} price={supplementPrice.price} />;
      }
      case 'PER_PERSON': {
        return <RenderRange text={'per person'} price={supplementPrice.price} />;
      }
      case 'FIXED_LIMIT': {
        return <RenderRange text={'required add-ons'} price={supplementPrice.price} />;
      }
      default:
        assertNever(supplementPrice.type);
    }
  };

  const isRequiredSupplementZero =
    price.supplements?.length === 0 ||
    every(price.supplements, (supplementPrice) => isZero(supplementPrice.price));

  const getRange = () => {
    if (isZero(price.pricePerPerson) && !isRequiredSupplementZero) {
      return map(
        price.supplements,
        (supplementPrice: consumer.OfferedSupplementPrice, idx: number) => {
          const priceRange: React.ReactNode = getPriceRangeForSupplementPrice(supplementPrice);
          return (
            <span key={idx}>
              {/* what is a good key to use here? ^^^^ */}
              {priceRange}
              {idx !== (price?.supplements?.length || 0) - 1 &&
                !isZero(supplementPrice.price) &&
                !isNil(supplementPrice.price) &&
                ', '}
            </span>
          );
        }
      );
    } else if (isZero(price.pricePerPerson)) {
      if (isEventType(type)) {
        return 'Free Event';
      } else {
        return 'Free';
      }
    }
    return <RenderRange text={name} price={price.pricePerPerson} />;
  };

  const name = getName();
  const range: React.ReactNode = getRange();

  return (
    <>
      <span>{range}</span>
      <span>
        {!isRequiredSupplementZero && !isZero(price.pricePerPerson) && <span>&nbsp;+ add-ons</span>}
      </span>
    </>
  );
};
